import { CaretDownOutlined }    from '@ant-design/icons';
import Button                   from '@widesk-ui/components/Button';
import EditableText             from '@widesk-ui/components/form/EditableText';
import Popover                  from '@widesk-ui/components/Popover';
import Text                     from '@widesk-ui/components/Text';
import Title                    from '@widesk-ui/components/Title';
import View                     from '@widesk-ui/components/View';
import { observer }             from 'mobx-react';
import { navigate }             from '@widesk-ui/hooks/useNavigate';
import { useEffect }            from 'react';
import { useState }             from 'react';
import useAccount               from '@/hooks/useAccount';
import useApiCollection         from '@widesk-core/hooks/useApiCollection';
import AccountModel             from '@/models/services/account/AccountModel';
import useDidMount              from '@widesk-core/hooks/useDidMount';
import UserAccountModel         from '@/models/services/account/UserAccountModel';
import appStore                 from '@/stores/appStore';
import { roleCan }              from '@/stores/accessStore';
import subscriptionStore        from '@/stores/subscriptionStore';
import useDesignTokens          from '@/hooks/useDesignTokens';
import AccountSubscriptionModel from '@models/account/AccountSubscriptionModel';
import { CheckCircleOutlined }  from '@ant-design/icons';
import useTheme                 from '@widesk-ui/hooks/useTheme';
import Spin                     from '@widesk-ui/components/Spin';
import useThemeMode             from '@widesk-ui/hooks/useThemeMode';
import SubscriptionModel        from '@models/account/SubscriptionModel';
import { EuroCircleFilled }     from '@ant-design/icons';
import { EuroOutlined }         from '@ant-design/icons';
import { CrownOutlined }        from '@ant-design/icons';
import { CrownFilled }          from '@ant-design/icons';
import { isFreePlan }           from '@/helpers/subscription/plans';
import { isOnDemandPlan }       from '@/helpers/subscription/plans';

function getSubscriptionIcon(subscription?: SubscriptionModel, filled?: boolean) {
	if (!subscription?.reference || isFreePlan(subscription)) return undefined;

	if (isOnDemandPlan(subscription)) return filled ? <CrownFilled /> : <CrownOutlined />;

	return filled ? <EuroCircleFilled /> : <EuroOutlined />;
}

const AccountSelector = observer(() => {
	const { mode } = useThemeMode();
	const theme = useTheme();
	const tokens = useDesignTokens();
	const collAccount = useApiCollection(AccountModel);
	const collUserAccount = useApiCollection(UserAccountModel);
	const collAccountSubscription = useApiCollection(AccountSubscriptionModel);

	useDidMount(async () => {
		await collUserAccount.listBy(appStore.user.urn, 'user');
		const accountIds = collUserAccount.map(userAccount => userAccount.getId('account'));

		return Promise.all([
			collAccount
				.setSort('name', 'asc')
				.setRequiredFilter('id', accountIds)
				.list(),
			collAccountSubscription
				.setRequiredFilter('account', accountIds)
				.list(),
			collUserAccount.whenIsLoaded(m => m.userAccountType),
		]);
	});

	const { account, accountUrn, selectAccount } = useAccount();
	const [lastLoadedPlan, setLastLoadedPlan] = useState<SubscriptionModel>();
	const subscriptionIcon = getSubscriptionIcon(lastLoadedPlan, true);

	const accountPlan = subscriptionStore.isLoaded
		? subscriptionStore.accountSubscription?.subscription
		: collAccountSubscription.findByResolvableUrn('account', accountUrn)?.subscription;

	const loadedPlan = accountPlan?.isLoaded ? accountPlan : undefined;

	const [open, setOpen] = useState(false);

	useEffect(() => {
		setLastLoadedPlan(undefined);
	}, [accountUrn]);

	useEffect(() => {
		if (loadedPlan) setLastLoadedPlan(loadedPlan);
	}, [loadedPlan]);

	useEffect(() => {
		setOpen(false);
	}, [accountUrn]);

	const handleOpenChange = (newOpen: boolean) => {
		setOpen(newOpen);
	};

	if (collAccount.length < 2) {
		return (
			<View center gapH="xxs" style={{ color: 'white' }} row>
				<Text>{account?.renderName()}</Text>
				-
				{subscriptionIcon}
				<Text>{lastLoadedPlan?.label}</Text>
			</View>
		);
	}

	return (
		<Popover
			trigger="click"
			open={open}
			onOpenChange={handleOpenChange}
			placement="bottomLeft"
			content={
				<View className="account-wall__container" gapV="xxs" widthF minW={200} maxW={400}>
					<View color={tokens.text.muted}>Choisissez un compte</View>
					<View maxH={300} overflow="auto" gapV="xs" style={{ scrollbarWidth: 'thin' }}>
						{collAccount.models.map(a => {
							const isCurrent = a.id === account?.id;
							const subscription = collAccountSubscription
								.findByResolvableId('account', a.id)?.subscription;
							const canRename = roleCan(
								collUserAccount.findByResolvableId('account', a.id)?.userAccountType.reference,
								'account',
							);

							return (
								<View
									color={isCurrent ? tokens.text.disabled : undefined}
									className="account-wall__card ant-btn ant-btn-default ant-btn-color-default ant-btn-variant-outlined "
									bg={isCurrent ? tokens.surface.disabled : undefined}
									key={a.id}
									onClick={async () => {
										navigate('/');
										await selectAccount(a.id);
										setOpen(false);
									}}
									left
									lineHeight={1}
									heightF
									style={{
										pointerEvents: isCurrent ? 'none' : undefined,
										gap: theme.marginSM,
										justifyContent: 'space-between',
										padding: theme.marginSM,
										textAlign: 'left',
									}}
									row
								>
									<View gapV={2}>
										<Title level={5} bold centerV>
											{canRename ? (
												<div onClick={e => e.stopPropagation()} style={{ pointerEvents: 'all' }}>
													<EditableText
														value={a.name}
														emptyLabel="Sans nom"
														size="small"
														onSubmit={async value => {
															// `EditableText` laisse le champ ouvert et affiche ce message quand
															// `onSubmit` lève : un nom vide se corrige sans perdre la saisie.
															if (!value) throw new Error('Le nom ne peut pas être vide.');

															await a.patch({ name: value });

															// Le compte courant est une AUTRE instance que celle de la collection :
															// sans ça, l'en-tête garderait l'ancien nom jusqu'au rechargement.
															if (isCurrent) account.set('name', value);
														}}
													/>
												</div>
											) : (a.name || 'Sans nom')}
										</Title>
										<View color={tokens.text.muted} gapH="xxs" row>
											{getSubscriptionIcon(subscription)}
											{subscription?.label}
										</View>
									</View>
									{isCurrent && (
										<View color={tokens.color.primary} size={18}>
											<CheckCircleOutlined />
										</View>
									)}
								</View>
							);
						})}
					</View>
				</View>
			}
		>
			<View>
				<Button
					className="widesk-account-selector"
					title="Changer de compte"
					tooltipProps={{
						color: mode === 'dark' ? undefined : tokens.surface.page,
						zIndex: 1000,
						placement: 'bottomLeft',
					}}
					ghost={mode !== 'dark'}
					style={{
						height: 'auto',
						padding: `${theme.marginXXS}px ${theme.marginXS}px`,
					}}
				>
					<View centerV gapH="xxl" row>
						<View centerV gapH="xs" row>
							{subscriptionIcon && (
								<View size={18}>{getSubscriptionIcon(lastLoadedPlan, true)}</View>
							)}
							<View left>
								{account?.isLoaded ? <span style={{ fontWeight: 600 }}>{account?.name}</span> : <Spin size="small" />}
								<View size={theme.sizeSM}>{lastLoadedPlan?.label}</View>
							</View>
						</View>
						<View centerV gapH="xxs" row>
							<View className="texte" width="0" overflow="hidden">Changer de compte</View>
							<CaretDownOutlined rotate={open ? 180 : 0} />
						</View>
					</View>
				</Button>
			</View>
		</Popover>
	);
});

export default AccountSelector;
